import { Mutator } from 'final-form';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Checkbox, Notification, Title } from '@xsolla/ui-core';
import { CrossCircle } from '@xsolla/ui-icons';
import { Modal } from '@xsolla/ui-modal';

import { connectMailchimp, getMailchimpAudience } from '../../../../api';
import { Audience } from '../../../../api/users/mailchimp/types';
import { MAILCHIMP_LINKS } from '../../../../constants';
import { useConfig } from '../../../../contexts/config';
import { useNotifications } from '../../../../hooks/useNotifications';
import { usePaths } from '../../../../routes/routesHelper';
import {
  CollapseWithTitle,
  FieldInput,
  FieldSelect,
  FormFooter,
  LinkPAStyle,
  LinkPAStyleUnderline,
  Preloader
} from '../../../ui';
import { FormValues } from '../types';
import { ExtraButtonStyled, FormWrapper } from './style';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  setMailchimpListId: (mailchimpListId: string) => void;
  projectId: string;
};

const ConnectModal = ({ onClose, onSubmit, projectId, setMailchimpListId }: Props): JSX.Element => {
  const { userSupplementaryInfoPath } = usePaths();
  const intl = useIntl();
  const { API, PERMISSION, AUDIENCE } = MAILCHIMP_LINKS;
  const { xsollaAnalytics } = useConfig();
  const { showErrorNotify } = useNotifications();

  const [audiences, setAudiences] = useState<Audience[] | null>(null);
  const [fetchingAudiences, setFetchingAudiences] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const submitForm = useCallback(
    (values: FormValues) => {
      setFetching(true);
      setError(false);

      const { apiKey, audience } = values;

      const listId = audiences?.find((item) => item.list_name === audience)?.list_id;

      if (listId) {
        setMailchimpListId(listId);
        connectMailchimp(projectId, { api_key: apiKey, list_id: listId })
          .then(() => {
            xsollaAnalytics.sendClick('login_pa_module__users_database__mailchimp', 'connected');
            onSubmit();
          })
          .catch((err) => {
            showErrorNotify(err);
            setError(true);
          })
          .finally(() => setFetching(false));
      } else {
        setError(true);
        setFetching(false);
      }
    },
    [audiences]
  );

  const handleApiKey = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value && value.length >= 36 && value.length <= 37) {
      setFetchingAudiences(true);
      setError(false);

      getMailchimpAudience(projectId, value)
        .then((res) => setAudiences(res))
        .catch((err) => {
          showErrorNotify(err);
          setError(true);
        })
        .finally(() => setFetchingAudiences(false));
    }
  }, []);

  const audienceDropdown = useMemo(() => {
    if (audiences && audiences.length !== 0) {
      return audiences.map((audience) => ({
        children: audience.list_name,
        value: audience.list_name
      }));
    }

    return [];
  }, [audiences]);

  const apiKeyValidator = () => (value: string) =>
    value && (value.length < 36 || value.length > 37)
      ? intl.formatMessage({ id: 'app.validation.mailchimp-key-length' })
      : undefined;

  const clearApiKey: Mutator<FormValues> = (_, state, utils) => {
    utils.changeValue(state, 'apiKey', () => '');
    utils.changeValue(state, 'audience', () => '');
    utils.changeValue(state, 'permission', () => false);
    setAudiences(null);
  };

  const renderInputExtra = useMemo(() => {
    if (fetchingAudiences) {
      return <Preloader />;
    }

    return <></>;
  }, [fetchingAudiences, audiences]);

  const renderEmptyAudience = useMemo(() => {
    return (
      <Notification
        caption={intl.formatMessage(
          { id: 'users.mailchimp.audience.empty.text' },
          {
            a: (chunk: string) => (
              <LinkPAStyleUnderline href={AUDIENCE} target="_blank" rel="noreferrer">
                {chunk}
              </LinkPAStyleUnderline>
            )
          }
        )}
        palette="brand">
        {intl.formatMessage({ id: 'users.mailchimp.audience.empty.title' })}
      </Notification>
    );
  }, []);

  const renderClearButton = (clear: () => void) => {
    return (
      <ExtraButtonStyled palette="muted" onClick={clear}>
        <CrossCircle />
      </ExtraButtonStyled>
    );
  };

  const renderAudience = useMemo(() => {
    return (
      <>
        <FieldSelect
          name="audience"
          label={intl.formatMessage({ id: 'users.mailchimp.audience.label' })}
          placeholder={intl.formatMessage({ id: 'button.select' })}
          options={audienceDropdown}
          disabled={!audiences || audiences.length === 0}
          data-test-id="mailchimp-field-select"
        />

        <Field name="permission" type="checkbox">
          {({ input }) => (
            <Checkbox
              {...input}
              disabled={!audiences || audiences.length === 0}
              data-test-id="permission-checkbox">
              {intl.formatMessage(
                {
                  id: 'users.mailchimp.confirm.label'
                },
                {
                  a: (chunk: string) => (
                    <LinkPAStyle href={PERMISSION} target="_blank" rel="noreferrer">
                      {chunk}
                    </LinkPAStyle>
                  )
                }
              )}
            </Checkbox>
          )}
        </Field>
      </>
    );
  }, [audiences]);

  return (
    <Modal>
      <Form onSubmit={submitForm} mutators={{ clearApiKey }}>
        {({ values, form: { mutators, submit } }) => {
          return (
            <div data-test-id="mailchimp-form">
              <Title>{intl.formatMessage({ id: 'users.mailchimp.head' })}</Title>
              <CollapseWithTitle
                title={intl.messages['users.mailchimp.notification.title']}
                instruction={intl.formatMessage(
                  {
                    id: 'users.mailchimp.notification.description.mailchimp-api-key'
                  },
                  {
                    br: () => <br />,
                    b: (chunk: string) => <b>{chunk}</b>,
                    a1: (chunk: string) => (
                      <LinkPAStyle href={API} target="_blank" rel="noreferrer">
                        {chunk}
                      </LinkPAStyle>
                    ),
                    a2: (chunk: string) => (
                      <LinkPAStyle href={AUDIENCE} target="_blank" rel="noreferrer">
                        {chunk}
                      </LinkPAStyle>
                    ),
                    a3: (chunk: string) => (
                      <LinkPAStyle
                        href={userSupplementaryInfoPath(projectId)}
                        target="_blank"
                        rel="noreferrer">
                        {chunk}
                      </LinkPAStyle>
                    )
                  }
                )}
              />

              <FormWrapper>
                <Field name="apiKey" validate={apiKeyValidator()}>
                  {({ input, meta }) => (
                    <FieldInput
                      {...input}
                      errorMessage={meta.touched && meta.error ? meta.error : ''}
                      label={intl.formatMessage({ id: 'users.mailchimp.key.label' })}
                      placeholder={intl.formatMessage({
                        id: 'users.mailchimp.key.placeholder'
                      })}
                      maxLength={37}
                      disabled={!!audiences || fetchingAudiences}
                      onChange={(e) => {
                        input.onChange(e);
                        handleApiKey(e);
                      }}
                      extra={() => renderInputExtra}
                      extraRight={audiences && renderClearButton(mutators.clearApiKey)}
                      extraAlign="right"
                      extraAllowClick
                    />
                  )}
                </Field>

                {audiences?.length === 0 ? renderEmptyAudience : renderAudience}
              </FormWrapper>

              <FormFooter
                isModal
                onSubmit={submit}
                onCancel={onClose}
                fetching={fetching}
                error={error}
                disabled={!values.audience || !values.permission}
                submitText={intl.formatMessage({
                  id: 'users.mailchimp.connect-form.button'
                })}
              />
            </div>
          );
        }}
      </Form>
    </Modal>
  );
};

export default ConnectModal;
